import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

/**
 * Loads Google Analytics 4 (`analytics.ga4_id`), Microsoft Clarity
 * (`analytics.clarity_id`) and Meta / Facebook Pixel (`analytics.meta_pixel_id`)
 * from the CMS site_content.
 *  - GA4: injects gtag.js once + fires page_view on every SPA route change.
 *  - Clarity: injects clarity.ms tag once; it handles route changes internally.
 *  - Meta Pixel: injects fbevents.js once + fires PageView on every route change.
 */
export default function Analytics() {
  const location = useLocation();
  const gaIdRef = useRef(null);
  const clarityIdRef = useRef(null);
  const pixelIdRef = useRef(null);
  const gaReadyRef = useRef(false);
  const pixelReadyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/content").then(({ data }) => {
      if (cancelled) return;
      const a = data?.analytics || {};

      // ---- Google Analytics 4 ----
      const gid = (a.ga4_id || "").trim();
      if (gid && /^G-[A-Z0-9]+$/i.test(gid) && gaIdRef.current !== gid) {
        gaIdRef.current = gid;
        if (!document.getElementById("ga4-script")) {
          const s = document.createElement("script");
          s.async = true;
          s.id = "ga4-script";
          s.src = `https://www.googletagmanager.com/gtag/js?id=${gid}`;
          document.head.appendChild(s);
          window.dataLayer = window.dataLayer || [];
          window.gtag = function () { window.dataLayer.push(arguments); };
          window.gtag("js", new Date());
          window.gtag("config", gid, { send_page_view: false });
          window.gtag("event", "page_view", {
            page_path: location.pathname + location.search,
            page_location: window.location.href,
            page_title: document.title,
          });
        }
        gaReadyRef.current = true;
      }

      // ---- Microsoft Clarity ----
      const cid = (a.clarity_id || "").trim();
      if (cid && /^[a-z0-9]{6,20}$/i.test(cid) && clarityIdRef.current !== cid) {
        clarityIdRef.current = cid;
        if (!document.getElementById("ms-clarity-script")) {
          (function (c, l, a, r, i, t, y) {
            c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
            t = l.createElement(r); t.async = 1; t.id = "ms-clarity-script";
            t.src = "https://www.clarity.ms/tag/" + i;
            y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
          })(window, document, "clarity", "script", cid);
        }
      }

      // ---- Meta / Facebook Pixel ----
      const pid = (a.meta_pixel_id || "").trim();
      if (pid && /^\d{6,20}$/.test(pid) && pixelIdRef.current !== pid) {
        pixelIdRef.current = pid;
        if (!document.getElementById("fb-pixel-script")) {
          // Official Meta Pixel snippet (inlined)
          /* eslint-disable */
          !function (f, b, e, v, n, t, s) {
            if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
            if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0";
            n.queue = []; t = b.createElement(e); t.async = !0; t.id = "fb-pixel-script";
            t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
          }(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
          /* eslint-enable */
          window.fbq("init", pid);
          window.fbq("track", "PageView");
          // <noscript> fallback for users without JS
          if (!document.getElementById("fb-pixel-noscript")) {
            const ns = document.createElement("noscript");
            ns.id = "fb-pixel-noscript";
            ns.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pid}&ev=PageView&noscript=1" />`;
            document.head.appendChild(ns);
          }
        }
        pixelReadyRef.current = true;
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Route-change page_view for GA4 + Meta Pixel (Clarity auto-tracks)
  useEffect(() => {
    if (gaReadyRef.current && window.gtag && gaIdRef.current) {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    if (pixelReadyRef.current && window.fbq) {
      window.fbq("track", "PageView");
    }
  }, [location.pathname, location.search]);

  return null;
}
